<template>
  <div class="change-limit">
    <div v-if="isLoading" class="change-limit__loader">
      <LimitLoader @finish="onLoaderFinished" />
      <div class="change-limit__loader_text">
        <div class="change-limit__loader_title">
          {{ $t`Мы подбираем для вас лимиты на выбор` }}...
        </div>
        <div class="change-limit__loader_hint">
          {{ $t`Пожалуйста, подождите` }}
        </div>
      </div>
    </div>
    <div v-else-if="isChangeSuccess" class="change-limit__accept">
      <LimitOptions
        v-if="limits.length > 1"
        :limits="formattedLimits"
        @limit-selected="confirmLimit"
        @limit-switch="onLimitSwitch"
        @limit-expand="onLimitExpand"
      >
        <template #btn-text>
          {{ $t`Выбрать новый лимит` }}
        </template>
      </LimitOptions>
      <template v-else>
        <CommonBanner
          :tagline="$t`Вы можете увеличить лимит на покупки до`"
          :title="$currencyFormatter(limitAmount)"
          type="neutral"
        />
        <CommonButton @click="confirmLimit(0)" spaced>
          <span
            v-html="
              $l10nformat($t`Увеличить лимит до #SUM#`, {
                SUM: $currencyFormatter(limitAmount),
              })
            "
          >
          </span>
        </CommonButton>
        <CommonButton
          class="return-btn"
          @click="$router.replace('/bank/red')"
          outlined
          spaced
        >
          <span
            v-html="
              $l10nformat($t`Оставить прежний лимит #SUM#`, {
                SUM: $currencyFormatter(currentLimit),
              })
            "
          >
          </span>
        </CommonButton>
        <AnswerTips questionPageName="KaspiRedLimitIncreasing" wrapped />
      </template>
    </div>
    <div v-else class="change-limit__reject">
      <CommonBanner type="neutral">
        <template #tagline>
          <div
            class="change-limit__reject_title"
            v-html="
              $t`К сожалению, мы пока</br>не можем увеличить</br>ваш лимит на покупки`
            "
          ></div>
        </template>
      </CommonBanner>
      <div class="change-limit__reject_hint text-size-m">
        {{ $t`Ваш лимит остался прежним` }} —
        <span class="text-nowrap">
          {{ $currencyFormatter(currentLimit) }}
        </span>
      </div>
      <CommonButton @click="$router.replace('/bank/red')" spaced>
        {{ $t`Вернуться в Мой Red` }}
      </CommonButton>
      <AnswerTips questionPageName="KaspiRedLimitIncrease" wrapped />
    </div>
  </div>
</template>

<script>
import { getAvailableLimit, getRedLimitStatus, setRedLimit } from "@/api/limit";
import LimitLoader from "@/components/limits/LimitLoader.vue";
import CommonBanner from "@/components/shared/banner/CommonBanner.vue";
import CommonButton from "@/components/shared/button/CommonButton.vue";
import AnswerTips from "@/components/shared/tips/AnswerTips.vue";
import LimitOptions from "@/components/limits/LimitOptions.vue";

export default {
  components: {
    LimitOptions,
    AnswerTips,
    CommonBanner,
    CommonButton,
    LimitLoader,
  },

  data() {
    return {
      isLoading: true,
      isChangeSuccess: false,
      limitStatus: null,
      limitData: null,
      limitAmount: -1,
      currentLimit: 0,
      limits: [],
    };
  },

  async created() {
    this.setCurrentLimit();
    const limitData = await getAvailableLimit();
    this.limitData = limitData;
  },

  beforeRouteLeave(to, from, next) {
    if (this.isLoading) {
      this.$logAnalyticEvent("mybank_red_increase_limit_cancel", {
        page_path: "",
        started_from: "",
      });
    }
    next();
  },

  computed: {
    buttonText() {
      return this.$l10nformat(this.$t`Увеличить лимит до #SUM#`, {
        SUM: this.$currencyFormatter(this.limitAmount),
      });
    },
    formattedLimits() {
      return this.limits?.map((val) => this.$currencyFormatter(val)) || [];
    },
  },

  methods: {
    onLimitExpand(isShrinked) {
      const event = isShrinked
        ? "mybank_red_increase_limit_offer_show_all"
        : "mybank_red_increase_limit_offer_hide_all";

      this.$logAnalyticEvent(event, {
        page_path: "",
        started_from: "",
      });
    },
    onLimitSwitch(val) {
      this.$logAnalyticEvent("mybank_red_increase_limit_selected", {
        limit: this.limits[val],
        page_path: "",
        started_from: "",
      });
    },
    async setCurrentLimit() {
      const bundle = await this.$store.dispatch("bundle/fetchBundle", {
        showLoader: false,
      });

      this.currentLimit = bundle.red_products[0].original_amount;
      this.$logAnalyticEvent("mybank_red_increase_limit_clicked", {
        limit: this.currentLimit,
        page_path: "",
        started_from: "",
      });
    },
    confirmLimit(i = 0) {
      this.limitAmount = this.limits[i];
      this.limitStatus.limit = this.limitAmount;
      this.setLimit();
    },
    async onLoaderFinished() {
      try {
        const { status_response, limits } = await getRedLimitStatus(
          this.limitData
        );
        this.limitStatus = status_response;
        this.isChangeSuccess =
          !status_response.is_reject && !status_response.is_error;
        this.limits = limits;

        if (this.limits.length === 1) {
          this.limitAmount = this.limits[0];
        }

        if (this.isChangeSuccess) {
          this.$logAnalyticEvent("mybank_red_increase_limit_offer_seen", {
            max_limit: status_response.limit,
            page_path: "",
            started_from: "",
          });
        } else {
          this.$logAnalyticEvent("mybank_red_increase_limit_rejected", {
            page_path: "",
            started_from: "",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
    async setLimit() {
      await setRedLimit(this.limitStatus);
      this.$logAnalyticEvent("mybank_red_increase_limit_approved", {
        limit: this.limitAmount,
        page_path: "",
        started_from: "",
      });
      this.$router.replace(
        `/bank/red/limits/success?limit=${this.limitAmount}`
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.change-limit {
  &__loader {
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 80px;

    &_text {
      padding: 30px 40px;
    }

    &_title {
      font-family: Roboto-Bold;
      font-size: 24px;
      line-height: 28px;
      margin-bottom: 10px;
    }

    &_hint {
      font-size: 12px;
      line-height: 14px;
      color: #a8a8a8;
    }
  }

  &__reject {
    &_hint {
      margin-top: 1px;
      text-align: left;
      padding: 22px 15px;
      background-color: #fff;
    }
  }
}

.guide-title {
  padding: 15px 15px 8px;
}

.return-btn {
  margin-top: -15px;
}
</style>
